import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { InvoiceStatusBadge } from '@/components/finances/InvoiceStatusBadge';
import { invoicesService, MarkInvoiceAsPaidRequest } from '@/services/invoices.service';
import { clientsService } from '@/services/clients.service';
import type { Invoice, Client, PaymentMethod, LineItemUnit } from '@/types';

// Map internal unit codes to the labels shown to users.
function formatUnitLabel(unit: LineItemUnit): string {
  const labels: Record<LineItemUnit, string> = {
    hours: 'hours',
    items: 'items',
    days: 'days',
    md: 'MD',
  };
  return labels[unit] || unit;
}

function InvoiceDetailContent() {
  const { id: invoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingPdf, setViewingPdf] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Mark as paid modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [paidAmount, setPaidAmount] = useState('');
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');

  useEffect(() => {
    if (invoiceId) {
      loadInvoiceData();
    }
  }, [invoiceId]);

  const loadInvoiceData = async () => {
    if (!invoiceId) return;

    try {
      setLoading(true);
      setError(null);

      const invoiceData = await invoicesService.getInvoice(invoiceId);
      setInvoice(invoiceData);

      if (invoiceData.clientId) {
        const clientData = await clientsService.getClient(invoiceData.clientId);
        setClient(clientData);
      }
    } catch (err: any) {
      console.error('Error loading invoice:', err);
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };


  const handleMarkAsPaid = async () => {
    if (!invoiceId) return;

    if (!paidAmount || parseFloat(paidAmount) <= 0) {
      alert('Please enter a valid paid amount');
      return;
    }

    try {
      const data: MarkInvoiceAsPaidRequest = {
        paidAmount: parseFloat(paidAmount),
        paidDate,
        paymentMethod,
      };

      await invoicesService.markInvoiceAsPaid(invoiceId, data);
      setShowPayModal(false);
      await loadInvoiceData();
    } catch (err: any) {
      console.error('Error marking invoice as paid:', err);
      alert(err.message || 'Failed to mark invoice as paid');
    }
  };

  const handleCancelInvoice = async () => {
    if (!invoiceId) return;
    if (!confirm('Are you sure you want to cancel this invoice?')) return;

    try {
      await invoicesService.cancelInvoice(invoiceId);
      await loadInvoiceData();
    } catch (err: any) {
      console.error('Error canceling invoice:', err);
      alert(err.message || 'Failed to cancel invoice');
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceId) return;
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.'))
      return;

    try {
      await invoicesService.deleteInvoice(invoiceId);
      navigate('/finances/invoices');
    } catch (err: any) {
      console.error('Error deleting invoice:', err);
      alert(err.message || 'Failed to delete invoice');
    }
  };

  const handleViewPdf = async () => {
    if (!invoiceId) return;

    try {
      setViewingPdf(true);
      const result = await invoicesService.generateInvoicePdf(invoiceId);
      window.open(result.pdfUrl, '_blank');
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      alert(err.message || 'Failed to generate PDF');
    } finally {
      setViewingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoiceId || !invoice) return;

    try {
      setDownloadingPdf(true);
      const result = await invoicesService.generateInvoicePdf(invoiceId);

      // Fetch the PDF as a blob and trigger download
      const response = await fetch(result.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      alert(err.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    const currency = client?.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-flowky-primary"></div>
          <p className="text-gray-600 mt-4">Loading invoice...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !invoice) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading invoice</h3>
          <p className="mt-1 text-sm text-gray-500">{error || 'Invoice not found'}</p>
          <Link
            to="/finances/invoices"
            className="mt-4 inline-block px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90"
          >
            Back to Invoices
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Breadcrumb
            items={[
              { label: 'Finances', href: '/finances' },
              { label: 'Invoices', href: '/finances/invoices' },
              { label: invoice.invoiceNumber },
            ]}
          />
          <div className="flex items-center gap-4 mt-2">
            <h1 className="text-3xl font-bold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
            <InvoiceStatusBadge status={invoice.status} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleViewPdf}
            disabled={viewingPdf || downloadingPdf}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {viewingPdf ? (
              <>
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                Opening...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View PDF
              </>
            )}
          </button>

          <button
            onClick={handleDownloadPdf}
            disabled={viewingPdf || downloadingPdf}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloadingPdf ? (
              <>
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download PDF
              </>
            )}
          </button>

          {(invoice.status === 'draft' || invoice.status === 'sent') && (
            <>
              <Link
                to={`/finances/invoices/${invoice.id}/edit`}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit
              </Link>
            </>
          )}

          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
            <button
              onClick={() => {
                setPaidAmount(invoice.total.toString());
                setShowPayModal(true);
              }}
              className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Mark as Paid
            </button>
          )}

          {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
            <button
              onClick={handleCancelInvoice}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              title="Cancel Invoice"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}

          {invoice.status === 'draft' && (
            <button
              onClick={handleDeleteInvoice}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              title="Delete Invoice"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Client Info */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h2>
          {client ? (
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Client Name</p>
                <p className="font-medium">{client.name}</p>
              </div>
              {client.contactInfo && (
                <div>
                  <p className="text-sm text-gray-600">Contact Information</p>
                  <p className="font-medium whitespace-pre-line">{client.contactInfo}</p>
                </div>
              )}
              {client.hourlyRate && (
                <div>
                  <p className="text-sm text-gray-600">Hourly Rate</p>
                  <p className="font-medium">{formatCurrency(client.hourlyRate)}/hour</p>
                </div>
              )}
              {client.country && (
                <div>
                  <p className="text-sm text-gray-600">Country</p>
                  <p className="font-medium">{client.country}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500">Client information not available</p>
          )}
        </div>

        {/* Invoice Info */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h2>
          <div className="space-y-2">
            <div>
              <p className="text-sm text-gray-600">Issue Date</p>
              <p className="font-medium">{formatDate(invoice.issueDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Due Date</p>
              <p className="font-medium">{formatDate(invoice.dueDate)}</p>
            </div>
            {(invoice.servicePeriodStart || invoice.servicePeriodEnd) && (
              <div>
                <p className="text-sm text-gray-600">Service Period</p>
                <p className="font-medium">
                  {invoice.servicePeriodStart && formatDate(invoice.servicePeriodStart)}
                  {invoice.servicePeriodStart && invoice.servicePeriodEnd && ' - '}
                  {invoice.servicePeriodEnd && formatDate(invoice.servicePeriodEnd)}
                </p>
              </div>
            )}
            {invoice.paidDate && (
              <div>
                <p className="text-sm text-gray-600">Paid Date</p>
                <p className="font-medium">{formatDate(invoice.paidDate)}</p>
              </div>
            )}
            {invoice.paymentMethod && (
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-medium capitalize">{invoice.paymentMethod.replace('_', ' ')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Amount Summary */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Amount Summary</h2>
          <div className="space-y-2">
            <div>
              <p className="text-sm text-gray-600">Subtotal</p>
              <p className="font-medium">{formatCurrency(invoice.subtotal)}</p>
            </div>
            {invoice.discount && invoice.discount > 0 && (
              <div>
                <p className="text-sm text-gray-600">Discount</p>
                <p className="font-medium text-green-600">-{formatCurrency(invoice.discount)}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Tax ({invoice.taxRate || 0}%)</p>
              <p className="font-medium">{formatCurrency(invoice.taxAmount || 0)}</p>
            </div>
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(invoice.total)}</p>
            </div>
            {invoice.paidAmount > 0 && (
              <>
                <div>
                  <p className="text-sm text-gray-600">Paid Amount</p>
                  <p className="font-medium text-green-600">{formatCurrency(invoice.paidAmount)}</p>
                </div>
                {invoice.status !== 'paid' && (
                  <div>
                    <p className="text-sm text-gray-600">Outstanding</p>
                    <p className="font-medium text-orange-600">
                      {formatCurrency(invoice.total - invoice.paidAmount)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="border border-border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoice.lineItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{item.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {item.quantity}
                      {item.unit ? ` ${formatUnitLabel(item.unit)}` : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(item.unitPrice)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(item.amount)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes and Terms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
          {invoice.notes ? (
            <p className="text-gray-700 whitespace-pre-line">{invoice.notes}</p>
          ) : (
            <p className="text-gray-400 italic">No notes added</p>
          )}
        </div>
        {invoice.termsAndConditions && (
          <div className="border border-border rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Terms & Conditions</h2>
            <p className="text-gray-700 whitespace-pre-line">{invoice.termsAndConditions}</p>
          </div>
        )}
      </div>

      {/* Mark as Paid Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="border border-border rounded-lg p-4 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Mark Invoice as Paid</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paid Amount
                </label>
                <input
                  type="number"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  step="0.01"
                  min="0"
                  max={invoice.total}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Paid Date</label>
                <input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option key="bank_transfer" value="bank_transfer">
                    Bank Transfer
                  </option>
                  <option key="credit_card" value="credit_card">
                    Credit Card
                  </option>
                  <option key="paypal" value="paypal">
                    PayPal
                  </option>
                  <option key="cash" value="cash">
                    Cash
                  </option>
                  <option key="check" value="check">
                    Check
                  </option>
                  <option key="other" value="other">
                    Other
                  </option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleMarkAsPaid}
                className="flex-1 px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
              >
                Confirm Payment
              </button>
              <button
                onClick={() => setShowPayModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default function InvoiceDetailPage() {
  return (
    <ProtectedRoute>
      <InvoiceDetailContent />
    </ProtectedRoute>
  );
}
